import React, {
  useState,
  useEffect,
  useRef,
  forwardRef,
  useImperativeHandle,
} from "react";
import styles from "css/Input.module.css";
import { Portal, Button } from "components";
import { motion, AnimatePresence } from "framer-motion";
import Lib from "library";

const Input = forwardRef(
  (
    {
      tabindex,
      id,
      name,
      onClick,
      value,
      defaultValue,
      className = "",
      type = "text",
      style,
      placeholder,
      readOnly,
      disabled,
      maxlength,
      onChange,
      onKeyPress,
      next,
    },
    ref
  ) => {
    const onKeyUp = (e) => {
      const value = e.target.value;
      if (next && maxlength && value.length >= maxlength) {
        if (type === "tel" && /\D|^$/.test(value)) return false;
        let nextElement;
        if (typeof next === "string") {
          nextElement = document.querySelector(next);
        } else if (typeof next === "function") {
          next(e);
        } else {
          nextElement = next;
        }
        if (nextElement) nextElement.focus();
      }
    };

    return (
      <input
        tabIndex={tabindex}
        onClick={onClick}
        onKeyPress={onKeyPress}
        ref={ref}
        onKeyUp={onKeyUp}
        name={name}
        onChange={onChange}
        id={id}
        className={`${styles.Input} ${className}`}
        type={type}
        placeholder={placeholder}
        style={style}
        readOnly={readOnly}
        disabled={disabled}
        maxLength={maxlength}
        autoComplete="off"
        value={value}
        defaultValue={defaultValue}
      />
    );
  }
);

const InputX = forwardRef(
  (
    {
      tabindex,
      id,
      name,
      onClick,
      focus,
      value,
      setValue,
      defaultValue,
      className = "",
      type = "text",
      style,
      placeholder,
      readOnly,
      disabled,
      maxlength,
      onChange,
      onKeyPress,
      onX,
      next,
      onBlur,
      inputMode,
    },
    ref
  ) => {
    const [showX, setShowX] = useState(false);
    const input = useRef(ref);

    const _className = Lib.getStyle(className, styles);

    const onKeyUp = (e) => {
      const value = input.current.value;
      /* if (value) {
			setShowX(true)
		} else {
			setShowX(false)
		} */

      if (next && maxlength && value.length >= maxlength) {
        if (type === "tel" && /\D|^$/.test(value)) return false;
        let nextElement;
        if (typeof next === "string") {
          nextElement = document.querySelector(next);
        } else if (typeof next === "function") {
          next(e);
        } else {
          nextElement = next;
        }
        if (nextElement) nextElement.focus();
      }
    };

    // 한글 입력 시 maxlength 제한
    const onInput = (e) => {
      if (!maxlength) return;
      
      const target = e.target;
      const value = target.value;
      
      // 한글 조합 문자를 고려한 실제 길이 계산
      const actualLength = Array.from(value).length;
      
      if (actualLength > maxlength) {
        // maxlength를 초과한 경우, 초과 부분을 제거
        const truncatedValue = Array.from(value).slice(0, maxlength).join('');
        target.value = truncatedValue;
        
        // onChange 이벤트가 있다면 업데이트된 값을 전달
        if (onChange) {
          const syntheticEvent = {
            ...e,
            target: {
              ...target,
              value: truncatedValue
            }
          };
          onChange(syntheticEvent);
        }
        
        // setValue가 있다면 상태도 업데이트
        if (setValue) {
          setValue(truncatedValue);
        }
      }
    };

    const onXClick = (e) => {
      if (input.current.getAttribute("disabled")) return;
      input.current.value = "";
      setValue && setValue("");
      setShowX(false);
      if (onX) onX(e);
      input.current.focus();
    };

    useEffect(() => {
      if (value) setShowX(true);
      else setShowX(false);
    }, [value]);

    useEffect(() => {
      if (focus) {
        setTimeout(() => {
          input?.current.focus();
        }, 200);
      }
      if (input?.current?.value) setShowX(true);

      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
      <div className={`${styles.wrap} ${_className}`} style={style}>
        <input
          tabIndex={tabindex}
          onClick={onClick}
          onKeyPress={onKeyPress}
          name={name}
          onChange={onChange}
          onInput={onInput}
          id={id}
          ref={input}
          onKeyUp={onKeyUp}
          className={`${styles.Input} ${showX ? styles.on : ""}`}
          type={type}
          placeholder={placeholder}
          readOnly={readOnly}
          disabled={disabled}
          maxLength={maxlength}
          autoComplete="off"
          value={value}
          defaultValue={defaultValue}
          onBlur={onBlur}
          inputMode={inputMode}
        />
        <i
          className={`icon_input_close ${styles.input_close}`}
          onClick={onXClick}
        />
      </div>
    );
  }
);

const Select = forwardRef(
  (
    {
      id,
      className,
      onClick,
      selected,
      title,
      style,
      placeholder,
      data,
      multi,
      onSelect,
      essential,
      name,
      disabled,
    },
    ref
  ) => {
    const select = useRef();

    useImperativeHandle(ref, () => ({ show: onSelectClick, setText: setText }));

    let selectedText;

    const [showOptions, setShowOptions] = useState(false);
    const [text, setText] = useState(selectedText);
    const input = useRef();

    const _className = className ? Lib.getStyle(className, styles) : "";

    const onSelectClick = () => {
      if (disabled) return;
      select.current.classList.add(styles.on);
      setShowOptions(true);
      if (onClick) onClick();
    };

    //#region Effects
    useEffect(() => {
      if (Lib.isNotNull(selected) && data) {
        data.forEach((item) => {
          if (item.value === selected) {
            setText(item.key);
          }
        });
      } else if (selected === null) {
        setText();
      }
    }, [selected, data]);

    useEffect(() => {
      if (showOptions) {
        select.current.classList.add(styles.on);
      } else {
        select.current.classList.remove(styles.on);
      }
    }, [showOptions]);
    //#endregion

    return (
      <>
        <div
          ref={select}
          id={id}
          className={`${styles.Select} ${_className} ${
            disabled ? styles.disabled : ""
          }`}
          style={style}
          onClick={onSelectClick}
        >
          {text ? (
            <span className="selectedText">{text}</span>
          ) : (
            <span className={styles.placeholder}>{placeholder}</span>
          )}
          <input
            type="hidden"
            name={name}
            ref={input}
            defaultValue={selected}
          />
        </div>
        <Options
          select={select}
          name={name}
          input={input}
          essential={essential}
          onSelect={onSelect}
          multi={multi}
          show={showOptions}
          setShow={setShowOptions}
          data={data}
          title={title}
          setText={setText}
          selected={selected}
        />
      </>
    );
  }
);

const Options = ({
  onSelect,
  selected,
  multi,
  show,
  data,
  setShow,
  title,
  setText,
  essential,
  input,
  select,
}) => {
  //#region Variables
  const div = useRef();
  const list = useRef();
  const [height, setHeight] = useState(300);
  const [set, setSet] = useState(false);
  //#endregion

  //#region Methods
  const onClose = () => {
    setShow(false);
  };

  const OnSelect = (key, value, index, all) => {
    if (multi) {
      const li = list.current.childNodes[index];
      if (all) {
        Lib.siblings(li).forEach((element) => {
          element.querySelector(".checkMark").classList.remove("on");
        });
        li.querySelector(".checkMark").classList.add("on");
      } else {
        const check = li.querySelector(".checkMark");
        check.classList.toggle("on");
        let count = 0;
        const children = li.parentElement.children;
        [...children].forEach((element) => {
          if (element.classList.contains("allLi")) {
            element.querySelector(".checkMark").classList.remove("on");
          } else {
            if (element.querySelector(".checkMark").classList.contains("on"))
              count++;
          }
        });

        if (count === children.length - 1) {
          [...children].forEach((element) => {
            if (element.classList.contains("allLi")) {
              element.querySelector(".checkMark").classList.add("on");
            } else {
              element.querySelector(".checkMark").classList.remove("on");
            }
          });
        }
      }
    } else {
      setText && setText(key);
      if (input && input.current) input.current.value = value;
      setTimeout(() => {
        setShow(false);
        onSelect && onSelect(key, value);
      }, 200);
    }
    select?.current?.classList.remove(styles.on);
  };

  const onButtonClick = () => {
    setShow(false);
    const selected = [];
    list.current.querySelectorAll(".checkMark.on").forEach((check) => {
      const key = check.getAttribute("data-key");
      const value = check.getAttribute("data-value");
      selected.push({
        key: key,
        value: value,
      });
    });
    let keys = [],
      values = [];
    selected.forEach((item) => {
      keys.push(item.key);
      values.push(item.value);
    });
    setText &&
      setText(() => {
        return keys.join(", ");
      });
    if (input && input.current) input.current.value = values.join(",");
    onSelect && onSelect(selected);
  };

  const getCheckOn = (item) => {
    let on = "";
    if (multi) {
      let selecteds = selected.toString().split(",");
      selecteds = selecteds.map((item) => item.trim());
      if (selecteds.indexOf(item.value.toString()) > -1) on = "on";
    }
    return `checkMark ${on}`;
  };
  //#endregion

  //#region Variants
  const listVariants = {
    hidden: {
      y: 0,
      transition: {
        type: "tween",
        duration: 0.2,
      },
    },
    show: {
      y: "-100%",
      transition: {
        type: "tween",
        duration: 0.2,
      },
    },
  };

  const layerVariants = {
    hidden: {
      backdropFilter: "blur(0px)",
      WebkitBackdropFilter: "blur(0px)",
      backgroundColor: "rgba(0, 0, 0, 0)",
    },
    show: {
      backdropFilter: "blur(5px)",
      WebkitBackdropFilter: "blur(5px)",
      backgroundColor: "rgba(0, 0, 0, 0.7)",
    },
  };
  //#endregion

  //#region Effects
  useEffect(() => {
    if (show) {
      setHeight(div.current.getBoundingClientRect().height);
      document.body.classList.add("overflow_hidden");
    } else {
      document.body.classList.remove("overflow_hidden");
    }
  }, [show]);

  useEffect(() => {
    setSet(true);
  }, [height]);
  //#endregion

  return (
    <Portal>
      <AnimatePresence>
        {show && (
          <motion.div
            className="layer"
            variants={layerVariants}
            initial="hidden"
            animate="show"
            exit="hidden"
          >
            <motion.div
              ref={div}
              variants={listVariants}
              className={`${styles.selectList} ${
                title ? styles.withTitle : ""
              }`}
              initial="hidden"
              animate="show"
              exit="hidden"
            >
              <div className={styles.title}>
                {title}
                {!essential && (
                  <i className="icon_select_close" onClick={onClose} />
                )}
              </div>
              <ul
                className={`${styles.ul} ${set && styles.ul_auto}`}
                ref={list}
              >
                {data &&
                  data.map((item, index) => (
                    <li
                      key={index}
                      onClick={() =>
                        OnSelect(item.key, item.value, index, item.all)
                      }
                      className={`${
                        !multi && selected === item.value ? styles.on : ""
                      }${item.all ? " allLi" : ""}`}
                    >
                      {item.key}
                      {multi ? (
                        <i
                          className={getCheckOn(item)}
                          data-key={item.key}
                          data-value={item.value}
                        />
                      ) : undefined}
                    </li>
                  ))}
              </ul>
              {multi && (
                <Button className="btn--wide mt_20" onClick={onButtonClick} />
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </Portal>
  );
};

const InputRed = ({ text, show, children }) => {
  return (
    <div className={styles.inputRed}>
      {children}
      <span className={`${styles.warn} ${show ? styles.on : ""}`}>{text}</span>
    </div>
  );
};

const InputTimer = ({ start = false, setStart, duration, onComplete }) => {
  const [seconds, setSeconds] = useState("0");
  const [minutes, setMinutes] = useState("00");
  const [count, setCount] = useState(duration * 1000);
  const timer = useRef();

  useEffect(() => {
    if (start) {
      setStart(false);
      if (timer.current) {
        clearInterval(timer.current);
      }
      timer.current = setInterval(() => {
        setCount((v) => v - 1000);
      }, 1000);
    }

    return () => {
      setCount(duration * 1000);
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [start, duration]);

  useEffect(() => {
    const d = new Date(count);
    setMinutes(d.getMinutes());
    setSeconds(Lib.leadingZero(d.getSeconds()));

    if (count <= 0) {
      clearInterval(timer.current);
      setMinutes();
      setSeconds();
      if (onComplete) onComplete();
    }

    return () => {
      setMinutes();
      setSeconds();
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [count]);

  return (
    <div className={styles.InputTimer}>
      {minutes} {seconds ? ":" : ""} {seconds}
    </div>
  );
};

export { InputX, Select, InputRed, InputTimer, Options as SelectList };
export default Input;
